import React, { useState, useEffect } from "react";
import {
  BookOpen,
  Sparkles,
  Target,
  Heart,
  MessageCircle,
  Quote,
  Loader2,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Copy,
  Check,
} from "lucide-react";
import { supabaseUrl } from "../lib/supabase";

const features = [
  {
    icon: Target,
    title: "Main Points",
    desc: "4 key takeaways distilled from your sermon",
  },
  {
    icon: BookOpen,
    title: "Key Scriptures",
    desc: "Referenced passages with context",
  },
  {
    icon: Heart,
    title: "Applications",
    desc: "Practical steps for daily living",
  },
  {
    icon: MessageCircle,
    title: "Discussion Questions",
    desc: "Ready-made small group questions",
  },
  {
    icon: Quote,
    title: "Shareable Quotes",
    desc: "Social media-ready insights",
  },
  {
    icon: Sparkles,
    title: "Bulletin Summary",
    desc: "A warm, inviting overview",
  },
];

const loadingMessages = [
  "Reading your sermon...",
  "Identifying key themes...",
  "Extracting main points...",
  "Building discussion questions...",
  "Creating your study guide...",
];

export function FreeStudyGuide() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Free Sermon Study Guide Generator | SermonBuild";

    const metaTags: HTMLMetaElement[] = [];
    const setMeta = (attr: string, key: string, content: string) => {
      const el = document.createElement("meta");
      el.setAttribute(attr, key);
      el.setAttribute("content", content);
      document.head.appendChild(el);
      metaTags.push(el);
    };

    setMeta(
      "name",
      "description",
      "Create a free AI-powered sermon study guide in seconds. Paste your sermon and get main points, key scriptures, discussion questions, practical applications, and shareable quotes for your congregation.",
    );
    setMeta(
      "name",
      "keywords",
      "sermon study guide, sermon notes, sermon summary, sermon outline, small group questions, bible study guide, church bulletin, pastor tools, sermon preparation, AI sermon tool, sermon discussion questions, free sermon tool, sermon resources, preaching tools",
    );
    setMeta(
      "property",
      "og:title",
      "Free Sermon Study Guide Generator | SermonBuild",
    );
    setMeta(
      "property",
      "og:description",
      "Paste your sermon and instantly generate a beautiful, shareable study guide with main points, scriptures, discussion questions, and more. Free for pastors and church leaders.",
    );
    setMeta("property", "og:type", "website");
    setMeta(
      "property",
      "og:url",
      "https://app.sermonbuild.com/free-study-guide",
    );
    setMeta("property", "og:site_name", "SermonBuild");

    const ldJson = document.createElement("script");
    ldJson.type = "application/ld+json";
    ldJson.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Free Sermon Study Guide Generator",
      url: "https://app.sermonbuild.com/free-study-guide",
      description:
        "AI-powered tool that generates comprehensive sermon study guides with main points, key scriptures, discussion questions, practical applications, and shareable quotes.",
      applicationCategory: "ReligiousApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      creator: {
        "@type": "Organization",
        name: "SermonBuild",
        url: "https://sermonbuild.com",
      },
      audience: {
        "@type": "Audience",
        audienceType: "Pastors, Church Leaders, Ministry Workers",
      },
    });
    document.head.appendChild(ldJson);

    return () => {
      document.title = prevTitle;
      metaTags.forEach((el) => el.remove());
      ldJson.remove();
    };
  }, []);

  const [title, setTitle] = useState("");
  const [scripture, setScripture] = useState("");
  const [content, setContent] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [success, setSuccess] = useState<{
    shareId: string;
    url: string;
  } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError("Please enter a sermon title.");
      return;
    }
    if (!content.trim() || content.trim().length < 200) {
      setError("Please paste your sermon content (at least 200 characters).");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setLoadingMessageIndex(0);

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) =>
        Math.min(prev + 1, loadingMessages.length - 1),
      );
    }, 3000);

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          type: "freeStudyGuide",
          content: content.trim(),
          title: title.trim(),
          scripture: scripture.trim() || null,
          youtubeUrl: youtubeUrl.trim() || null,
          email: email.trim().toLowerCase(),
          firstName: firstName.trim() || null,
          theme,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error ||
            "Failed to generate study guide. Please try again.",
        );
      }

      const data = await response.json();

      if (data.shareId) {
        const studyUrl = `${window.location.origin}/study/${data.shareId}`;
        setSuccess({ shareId: data.shareId, url: studyUrl });
      } else {
        throw new Error("Study guide was created but no link was returned.");
      }
    } catch (err) {
      console.error("Error generating study guide:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!success) return;
    try {
      await navigator.clipboard.writeText(success.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = success.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#12171E] to-[#1C2B39]">
        <header className="px-6 py-6 flex items-center justify-between max-w-7xl mx-auto">
          <a href="https://sermonbuild.com" className="flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-[#D9A441]" />
            <span
              className="text-xl font-bold text-white tracking-tight"
              style={{ fontFamily: "DM Serif Display, serif" }}
            >
              SermonBuild
            </span>
          </a>
        </header>

        <main className="px-6 py-16 max-w-2xl mx-auto text-center">
          <div className="bg-[#1a2332] border border-[#2a3a4a] rounded-2xl p-10">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h1
              className="text-3xl font-bold text-white mb-3"
              style={{ fontFamily: "DM Serif Display, serif" }}
            >
              Your Study Guide is Ready!
            </h1>
            <p className="text-[#8a9bb0] mb-8">
              Share this link with your congregation so they can engage deeper
              with your sermon.
            </p>

            <div className="flex items-center gap-2 bg-[#12171E] border border-[#2a3a4a] rounded-lg p-3 mb-6">
              <input
                type="text"
                readOnly
                value={success.url}
                className="flex-1 bg-transparent text-white text-sm outline-none"
              />
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#D9A441] text-[#12171E] font-semibold text-sm hover:bg-[#e0b355] transition-colors"
              >
                {linkCopied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {linkCopied ? "Copied!" : "Copy"}
              </button>
            </div>

            <a
              href={`/study/${success.shareId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#D9A441] text-[#12171E] font-semibold hover:bg-[#e0b355] transition-colors mb-8"
            >
              View Study Guide <ArrowRight className="h-4 w-4" />
            </a>

            <div className="border-t border-[#2a3a4a] pt-8 mt-8">
              <p className="text-[#8a9bb0] text-sm mb-4">
                Want to create study guides for every sermon — plus AI writing
                tools, sermon management, and more?
              </p>
              <a
                href="https://sermonbuild.com"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[#D9A441] text-[#D9A441] font-semibold hover:bg-[#D9A441]/10 transition-colors"
              >
                Start Your Free 14-Day Trial <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#12171E] to-[#1C2B39]">
      <header className="px-6 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <a href="https://sermonbuild.com" className="flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-[#D9A441]" />
          <span
            className="text-xl font-bold text-white tracking-tight"
            style={{ fontFamily: "DM Serif Display, serif" }}
          >
            SermonBuild
          </span>
        </a>
        <a
          href="https://app.sermonbuild.com"
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#D9A441]/40 text-[#D9A441] text-sm font-medium hover:bg-[#D9A441]/10 transition-colors"
        >
          Sign Up Free <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </header>

      <main className="px-6 pt-10 pb-20 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D9A441]/10 border border-[#D9A441]/20 mb-6">
            <Sparkles className="h-4 w-4 text-[#D9A441]" />
            <span className="text-[#D9A441] text-sm font-medium">
              Free AI-Powered Tool
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight"
            style={{ fontFamily: "DM Serif Display, serif" }}
          >
            Create a Sermon Study Guide
            <span className="block text-[#D9A441]">in Seconds</span>
          </h1>
          <p className="text-lg text-[#8a9bb0] max-w-2xl mx-auto">
            Paste your sermon below and our AI will generate a beautiful,
            shareable study guide with main points, scriptures, discussion
            questions, and more.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 max-w-6xl mx-auto">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                    Sermon Title <span className="text-[#B22A2F]">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Walking by Faith"
                    className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                    Scripture Reference
                  </label>
                  <input
                    type="text"
                    value={scripture}
                    onChange={(e) => setScripture(e.target.value)}
                    placeholder="e.g., Hebrews 11:1-6"
                    className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                  Sermon Content <span className="text-[#B22A2F]">*</span>
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your full sermon text here..."
                  rows={12}
                  className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors resize-y"
                  disabled={loading}
                />
                <p className="text-xs text-[#4a5a6a] mt-1">
                  {content.length.toLocaleString()} characters{" "}
                  {content.length < 200 && content.length > 0
                    ? "(minimum 200)"
                    : ""}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                  YouTube Link{" "}
                  <span className="text-[#4a5a6a] text-xs font-normal">
                    (optional)
                  </span>
                </label>
                <input
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors"
                  disabled={loading}
                />
                <p className="text-xs text-[#4a5a6a] mt-1">
                  If provided, the video will be embedded in your study guide
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                  Study Guide Theme
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTheme("dark")}
                    disabled={loading}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                      theme === "dark"
                        ? "border-[#D9A441] bg-[#D9A441]/10"
                        : "border-[#2a3a4a] bg-[#1a2332] hover:border-[#3a4a5a]"
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-md flex-shrink-0 border border-[#3a4a3a]"
                      style={{
                        background: "linear-gradient(135deg, #2a3a2a, #1a2a1a)",
                      }}
                    />
                    <div className="text-left">
                      <p
                        className={`text-sm font-medium ${theme === "dark" ? "text-[#D9A441]" : "text-white"}`}
                      >
                        Dark
                      </p>
                      <p className="text-xs text-[#4a5a6a]">
                        Olive & green tones
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme("light")}
                    disabled={loading}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                      theme === "light"
                        ? "border-[#D9A441] bg-[#D9A441]/10"
                        : "border-[#2a3a4a] bg-[#1a2332] hover:border-[#3a4a5a]"
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-md flex-shrink-0 border border-[#d0c8b8]"
                      style={{
                        background: "linear-gradient(135deg, #f5f0e8, #e8e0d0)",
                      }}
                    />
                    <div className="text-left">
                      <p
                        className={`text-sm font-medium ${theme === "light" ? "text-[#D9A441]" : "text-white"}`}
                      >
                        Light
                      </p>
                      <p className="text-xs text-[#4a5a6a]">
                        Ivory & navy tones
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                    Your Email <span className="text-[#B22A2F]">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="pastor@church.org"
                    className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8a9bb0] mb-1.5">
                    First Name{" "}
                    <span className="text-[#4a5a6a] text-xs font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your first name"
                    className="w-full px-4 py-3 rounded-lg bg-[#1a2332] border border-[#2a3a4a] text-white placeholder-[#4a5a6a] outline-none focus:border-[#D9A441]/50 transition-colors"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#B22A2F]/10 border border-[#B22A2F]/30">
                  <AlertCircle className="h-4 w-4 text-[#B22A2F] flex-shrink-0" />
                  <p className="text-sm text-[#B22A2F]">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#D9A441] text-[#12171E] font-bold text-lg hover:bg-[#e0b355] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {loadingMessages[loadingMessageIndex]}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Generate Study Guide
                  </>
                )}
              </button>

              <p className="text-xs text-[#4a5a6a] text-center">
                By submitting, you agree to receive occasional emails about
                SermonBuild. Unsubscribe anytime.
              </p>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-[#1a2332] border border-[#2a3a4a] rounded-2xl p-6 sticky top-6">
              <h3
                className="text-lg font-bold text-white mb-1"
                style={{ fontFamily: "DM Serif Display, serif" }}
              >
                What You'll Get
              </h3>
              <p className="text-sm text-[#6a7a8a] mb-5">
                A beautiful, shareable study page including:
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#D9A441]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <feature.icon className="h-4 w-4 text-[#D9A441]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {feature.title}
                      </p>
                      <p className="text-xs text-[#6a7a8a]">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-[#2a3a4a]">
                <p className="text-xs text-[#6a7a8a] mb-2 font-medium">
                  PLUS WITH A FREE TRIAL TO SermonBuild:
                </p>
                <ul className="space-y-2">
                  {[
                    "AI sermon writing assistant",
                    "Sermon media & image generation",
                    "Presentation (Preach) mode",
                    "Full sermon library management",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-xs text-[#8a9bb0]"
                    >
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="px-6 py-8 border-t border-[#1a2332]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#D9A441]" />
            <span className="text-sm text-[#4a5a6a]">SermonBuild</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#4a5a6a]">
            <a
              href="/privacy-policy.html"
              className="hover:text-[#8a9bb0] transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/terms-of-service.html"
              className="hover:text-[#8a9bb0] transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
